import { useState } from "react";
import { format, parseISO } from "date-fns";
import { CalendarIcon, X } from "lucide-react";
import { Pill } from "@/components/AnswerBits";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export const TH = "px-4 py-2.5 text-left text-xs font-semibold text-[#6D7175] bg-[#F7F7F8] border-b border-[#E1E3E5] whitespace-nowrap";
export const TD = "px-4 py-3 align-middle border-b border-[#EDEEF0] text-sm text-[#303030]";

export function Page({ title, subtitle, actions, children, width = "max-w-6xl" }) {
  return (
    <div className="polaris min-h-full bg-background">
      <div className={`${width} mx-auto px-3 sm:px-6 py-5 sm:py-7`}>
        <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-semibold text-[#202223]" data-testid="page-title">{title}</h1>
            {subtitle && <p className="text-sm text-[#6D7175] mt-1">{subtitle}</p>}
          </div>
          {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
        </div>
        {children}
      </div>
    </div>
  );
}

export function Card({ children, className = "", ...rest }) {
  return (
    <div className={`bg-white border border-[#E1E3E5] rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden ${className}`} {...rest}>
      {children}
    </div>
  );
}

export function EmptyState({ icon: Icon, children, ...rest }) {
  return (
    <Card className="p-10 sm:p-12 text-center" {...rest}>
      {Icon && <Icon className="h-9 w-9 mx-auto text-[#8C9196]" />}
      <p className="mt-3 text-sm text-[#6D7175]">{children}</p>
    </Card>
  );
}

const STATUS_TONE = {
  indexed: "success", completed: "success",
  queued: "warning", running: "warning", cancelling: "warning",
  failed: "critical", cancelled: "neutral",
};

export function StatusPill({ status, ...rest }) {
  const label = status ? status.charAt(0).toUpperCase() + status.slice(1) : "Unknown";
  return <Pill tone={STATUS_TONE[status] || "neutral"} {...rest}>{label}</Pill>;
}

// Date picker storing "yyyy-MM-dd" strings (same format the API already expects).
export function DatePicker({ value, onChange, testid, placeholder = "Pick a date" }) {
  const [open, setOpen] = useState(false);
  const date = value ? parseISO(value) : undefined;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative">
        <PopoverTrigger asChild>
          <button type="button" data-testid={testid}
            className="w-full h-10 flex items-center gap-2 rounded-lg border border-input bg-white px-3 text-sm text-left hover:bg-[#F7F7F8] focus:outline-none focus:ring-2 focus:ring-[#1E7FC2]/30 transition-colors">
            <CalendarIcon className="h-4 w-4 text-[#6D7175] shrink-0" />
            <span className={date ? "text-[#202223]" : "text-[#8C9196]"}>{date ? format(date, "MMM d, yyyy") : placeholder}</span>
          </button>
        </PopoverTrigger>
        {date && (
          <button type="button" onClick={() => onChange("")} data-testid={`${testid}-clear`} aria-label="Clear date"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-[#6D7175] hover:bg-[#F1F2F4]">
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <PopoverContent className="w-auto p-0 font-polaris" align="start">
        <Calendar mode="single" selected={date} defaultMonth={date}
          onSelect={(d) => { onChange(d ? format(d, "yyyy-MM-dd") : ""); setOpen(false); }}
          initialFocus data-testid={`${testid}-calendar`} />
      </PopoverContent>
    </Popover>
  );
}
